/**
 * AtlasSkyTrafficModule — ambient ship flybys over the Commons: every couple
 * of minutes a ship crosses the sky near the player, high overhead, and
 * disappears into the distance. Pure atmosphere (SWG's living-world skies):
 * client-side, cosmetic, no server state — the schedule derives from the
 * wall clock so flybys feel regular but never metronomic, while the PATH is
 * anchored to the local player when the flight spawns (each traveler sees
 * their own sky traffic — set dressing, not shared world state).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeSystem, Phase } from '../engine-compat.js';
import type { AssetModule } from './AssetModule.js';

/** A flyby window opens roughly every 2.5 min (hash-jittered inside). */
const FLIGHT_INTERVAL_S = 150;
const PATH_HALF_M = 3400;   // spawn/despawn distance along the path (fog covers the ends)
const SHIP_SCALE = 2.2;

function h01(a: number, salt: number): number {
  let h = 0x811c9dc5 ^ a;
  h = Math.imul(h, 0x01000193) ^ salt;
  h = Math.imul(h, 0x01000193);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export class AtlasSkyTrafficModule implements IModule {
  readonly name = 'AtlasSkyTraffic';

  private app: App | null = null;
  private master: THREE.Object3D | null = null;
  private loading = false;
  private ship: THREE.Object3D | null = null;
  private flight: {
    k: number; t0: number; dur: number;
    sx: number; sy: number; sz: number;   // path start
    dx: number; dz: number;               // unit direction
    speed: number;
  } | null = null;

  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('SkyTraffic', Phase.RenderSub, 38, () => this.update()));
  }

  private ensureMaster(): void {
    if (this.master || this.loading) return;
    const conn = atlas.conn;
    if (!conn) return;
    let assetId = 0n;
    try {
      for (const pm of conn.db.prefabModel.iter()) {
        if (pm.prefabKey === 'structure:civa_ship') { assetId = pm.assetId; break; }
      }
    } catch { return; }
    if (assetId === 0n) return;
    this.loading = true;
    this.assets.load(assetId)
      .then((obj) => { this.master = obj; })
      .catch(() => { /* retry next flight window */ })
      .finally(() => { this.loading = false; });
  }

  private update(): void {
    // ONE-LINER LAW (sky-ship wave 2026-09-01): the flypast reused the SAME
    // civa_ship hull that now hangs over every city as the boardable
    // sky-ship — a second, random, unboardable copy crossing the sky read as
    // "the ship you added". Retired; the module stays for a future distinct
    // traffic chassis.
    const SKY_TRAFFIC_ENABLED = false;
    if (!SKY_TRAFFIC_ENABLED) { this.endFlight(); return; }
    // Sky traffic belongs to the Commons ground game only.
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
    if (!inCommons) { this.endFlight(); return; }
    this.ensureMaster();
    if (!this.master || !this.app) return;

    const now = Date.now() / 1000;
    const k = Math.floor(now / FLIGHT_INTERVAL_S);
    const start = k * FLIGHT_INTERVAL_S + h01(k, 11) * 45;

    if (this.flight && this.flight.k !== k) this.endFlight();
    if (!this.flight) {
      if (now < start) return;
      // Spawn the flight: a straight pass whose closest approach skims the
      // player — sometimes nearly overhead, sometimes a distant silhouette.
      const bearing = h01(k, 23) * Math.PI * 2;
      const dxd = Math.cos(bearing), dzd = Math.sin(bearing);
      const side = h01(k, 31) < 0.5 ? -1 : 1;
      const lateral = side * (90 + h01(k, 41) * 420);
      // HIGH ROAD LAW (sky-ship wave, the Ostorch report): 240–560 m sat
      // INSIDE the supertall band (crowns reach 560 m) — the flypast hull
      // carved straight through city towers every 2.5 minutes. Traffic now
      // rides far above both the skyline and the parked sky-ships (760 m).
      const alt = 1050 + h01(k, 53) * 400;
      const speed = 90 + h01(k, 61) * 70;
      const px = atlas.localX, pz = atlas.localZ;
      const cx = px - dzd * lateral;   // offset perpendicular to travel
      const cz = pz + dxd * lateral;
      const dur = (PATH_HALF_M * 2) / speed;
      if (now > start + dur) return;   // this window's pass already finished
      this.flight = {
        k, t0: start, dur,
        sx: cx - dxd * PATH_HALF_M, sy: alt, sz: cz - dzd * PATH_HALF_M,
        dx: dxd, dz: dzd, speed,
      };
      if (!this.ship) {
        this.ship = this.master.clone();
        this.ship.scale.setScalar(SHIP_SCALE);
        this.app.world.scene.add(this.ship);
      }
      // Hull noses point −X: face the direction of travel.
      this.ship.rotation.set(0, Math.atan2(dzd, -dxd), 0);
      this.ship.visible = true;
    }

    const f = this.flight;
    const t = now - f.t0;
    if (t > f.dur) { this.endFlight(); return; }
    const d = f.speed * t;
    // Altitude relative to the PLAYER's current height — the pass stays
    // overhead even on high ground.
    this.ship!.position.set(f.sx + f.dx * d, atlas.localY + f.sy, f.sz + f.dz * d);
  }

  private endFlight(): void {
    this.flight = null;
    if (this.ship) this.ship.visible = false;
  }

  dispose(): void {
    if (this.ship) { this.app?.world.scene.remove(this.ship); this.ship = null; }
  }
}
